/**
 * Debounced directory search across people (identities) and Spaces,
 * plus empty-state random discover samples.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createApiClient,
  type PublicIdentity,
  type PublicSpace,
} from '@adieuu/shared';
import { useAppConfig } from '../config';

const DEFAULT_DEBOUNCE_MS = 300;
const DEFAULT_MIN_QUERY = 2;
const DEFAULT_SEARCH_LIMIT = 50;
const DEFAULT_DISCOVER_LIMIT = 12;

export interface UseDirectorySearchOptions {
  debounceMs?: number;
  minQueryLength?: number;
  searchLimit?: number;
  discoverLimit?: number;
  /** When false, skip empty-state discover sampling (e.g. sidebar typeahead). Default true. */
  enableDiscover?: boolean;
  /**
   * When false, skip Spaces discover/search (Search page uses SearchSpacesDirectory).
   * Default true for sidebar typeahead.
   */
  enableSpaces?: boolean;
}

export interface UseDirectorySearchResult {
  query: string;
  people: PublicIdentity[];
  spaces: PublicSpace[];
  discoverPeople: PublicIdentity[];
  discoverSpaces: PublicSpace[];
  isSearching: boolean;
  isDiscoverLoading: boolean;
  searchError: string | null;
  discoverError: string | null;
  search: (query: string) => void;
  clear: () => void;
}

export function useDirectorySearch(
  options: UseDirectorySearchOptions = {},
): UseDirectorySearchResult {
  const {
    debounceMs = DEFAULT_DEBOUNCE_MS,
    minQueryLength = DEFAULT_MIN_QUERY,
    searchLimit = DEFAULT_SEARCH_LIMIT,
    discoverLimit = DEFAULT_DISCOVER_LIMIT,
    enableDiscover = true,
    enableSpaces = true,
  } = options;

  const { apiBaseUrl } = useAppConfig();
  const api = useMemo(() => createApiClient({ baseUrl: apiBaseUrl }), [apiBaseUrl]);

  const [query, setQuery] = useState('');
  const [people, setPeople] = useState<PublicIdentity[]>([]);
  const [spaces, setSpaces] = useState<PublicSpace[]>([]);
  const [discoverPeople, setDiscoverPeople] = useState<PublicIdentity[]>([]);
  const [discoverSpaces, setDiscoverSpaces] = useState<PublicSpace[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isDiscoverLoading, setIsDiscoverLoading] = useState(enableDiscover);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [discoverError, setDiscoverError] = useState<string | null>(null);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchSeq = useRef(0);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      searchSeq.current += 1;
    };
  }, []);

  useEffect(() => {
    if (!enableDiscover) {
      setIsDiscoverLoading(false);
      return;
    }

    let cancelled = false;
    setIsDiscoverLoading(true);
    setDiscoverError(null);

    void (async () => {
      try {
        const peopleRes = await api.identity.discover(discoverLimit);
        const spacesRes = enableSpaces
          ? await api.spaces.discover({ limit: discoverLimit, sample: true })
          : null;
        if (cancelled) return;
        if (peopleRes.success && peopleRes.data) {
          setDiscoverPeople(peopleRes.data);
        }
        if (spacesRes?.success && spacesRes.data) {
          setDiscoverSpaces(spacesRes.data.spaces);
        }
        if (!peopleRes.success && (!spacesRes || !spacesRes.success)) {
          setDiscoverError(
            peopleRes.error?.message ?? spacesRes?.error?.message ?? 'Discover failed',
          );
        }
      } catch {
        if (!cancelled) setDiscoverError('Discover failed');
      } finally {
        if (!cancelled) setIsDiscoverLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [api, discoverLimit, enableDiscover, enableSpaces]);

  const clear = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    searchSeq.current += 1;
    setQuery('');
    setPeople([]);
    setSpaces([]);
    setSearchError(null);
    setIsSearching(false);
  }, []);

  const search = useCallback(
    (searchQuery: string) => {
      const trimmed = searchQuery.trim();
      setQuery(trimmed);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (trimmed.length < minQueryLength) {
        searchSeq.current += 1;
        setPeople([]);
        setSpaces([]);
        setSearchError(null);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      setSearchError(null);
      const seq = ++searchSeq.current;

      debounceTimerRef.current = setTimeout(async () => {
        try {
          const peopleRes = await api.identity.search(trimmed, searchLimit);
          const spacesRes = enableSpaces
            ? await api.spaces.discover({ q: trimmed, limit: searchLimit })
            : null;
          if (seq !== searchSeq.current) return;

          const nextPeople = peopleRes.success && peopleRes.data ? peopleRes.data : [];
          const nextSpaces =
            spacesRes?.success && spacesRes.data ? spacesRes.data.spaces : [];
          setPeople(nextPeople);
          setSpaces(nextSpaces);

          if (!peopleRes.success && (!spacesRes || !spacesRes.success)) {
            setSearchError(
              peopleRes.error?.message ?? spacesRes?.error?.message ?? 'Search failed',
            );
          } else {
            setSearchError(null);
          }
        } catch {
          if (seq === searchSeq.current) {
            setPeople([]);
            setSpaces([]);
            setSearchError('Search failed');
          }
        } finally {
          if (seq === searchSeq.current) setIsSearching(false);
        }
      }, debounceMs);
    },
    [api, debounceMs, minQueryLength, searchLimit, enableSpaces],
  );

  return {
    query,
    people,
    spaces,
    discoverPeople,
    discoverSpaces,
    isSearching,
    isDiscoverLoading,
    searchError,
    discoverError,
    search,
    clear,
  };
}
